import type { AxiosInstance } from 'axios';
import type { Model, Feature, Checkpoint, ModelVersion } from '../../types/Model';
import type { BettingStrategy } from '../../types/BettingStrategy';
import type { Stat } from '../../types/Stat';
import { ModelSchema, CheckpointSchema, ModelVersionSchema } from '../../types/Model';
import {
  waitForResourceCreation,
  waitForResourceDeletion,
  waitForResourceUpdate,
  waitForFieldValue
} from '../../utils/apiRetryUtils';
import { timeAsync } from '../../utils/timingLogger';

export interface ModelCreateRequest {
  labId: string;
  domainId: string;
  outcomeId: string;
  name: string;
  description: string;
  picture: string;
  visibility?: string;
  handle?: string;
}

export interface ModelUpdateRequest {
  id: string;
  name?: string;
  description?: string;
  picture?: string;
  visibility?: string;
  handle?: string;
}

export interface ModelVersionRequest {
  status: string;
}

interface PaginationData {
  totalItems: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
}

interface ModelsResponse {
  items: Model[];
  pagination: PaginationData;
}

export interface FeatureRequest {
  name: string;
  description?: string;
  formula: string;
}

export interface CheckpointRequest {
  versionId: string;
  type: string;
  features: Feature[];
  suppressEvent?: boolean;
}

export interface PublicModelsQuery {
  search?: string;
  domainId?: string;
  outcomeId?: string;
  limit?: number;
  cursor?: string;
}

export const ModelService = (api: AxiosInstance) => ({

  // Get public models (no lab required)
  getPublicModels: async (params?: PublicModelsQuery) => {
    try {
      const response = await api.get<ModelsResponse>(`/models`, { params });
      return response.data;
    } catch (error) {
      console.error('Error getting public models:', error);
      return { items: [], pagination: { totalItems: 0, totalPages: 0, pageNumber: 1, pageSize: 20 } };
    }
  },

  // Get models for a given lab
  getLabModels: async (id: string, searchTerm?: string) => {
    try {
      const allModels: Model[] = [];
      let pageNumber = 1;
      let totalPages = 1;

      // Fetch all pages
      while (pageNumber <= totalPages) {
        const params: Record<string, string | number> = { pageNumber, pageSize: 20 };
        if (searchTerm) {
          params.search = searchTerm;
        }

        const response = await api.get<ModelsResponse>(`/labs/${id}/models`, { params });

        allModels.push(...response.data.items);
        totalPages = response.data.pagination.totalPages;
        pageNumber++;
      }

      return { items: allModels, pagination: { totalItems: allModels.length, totalPages, pageNumber: 1, pageSize: allModels.length } };
    } catch (error) {
      console.error('Error getting lab models:', error);
      return { items: [], pagination: { totalItems: 0, totalPages: 0, pageNumber: 1, pageSize: 20 } };
    }
  },

  // Get models for a given lab
  getLabModelsWithVersions: async (id: string, searchTerm?: string) => {
    try {
      const allModels: Model[] = [];
      let pageNumber = 1;
      let totalPages = 1;

      // Fetch all pages
      while (pageNumber <= totalPages) {
        const params: Record<string, string | number> = { pageNumber, pageSize: 20 };
        if (searchTerm) {
          params.search = searchTerm;
        }

        const response = await api.get<ModelsResponse>(`/labs/${id}/models`, { params });

        allModels.push(...response.data.items);
        totalPages = response.data.pagination.totalPages;
        pageNumber++;
      }

      // Get the versions for each model
      for (const model of allModels) {
        const versions = await api.get<{items: ModelVersion[]}>(`/models/${model.id}/versions?sort=created:desc`);
        //clean out records where id contains '|ckpt.'
        model.versions = versions.data.items.filter(version => !version.id.includes('|ckpt.'));
      }

      return { items: allModels, pagination: { totalItems: allModels.length, totalPages, pageNumber: 1, pageSize: allModels.length } };
    } catch (error) {
      console.error('Error getting lab models:', error);
      return { items: [], pagination: { totalItems: 0, totalPages: 0, pageNumber: 1, pageSize: 20 } };
    }
  },

  // Get models for a given lab with latest version (Phase 0 - optimized)
  getLabModelsWithLatestVersion: async (id: string, searchTerm?: string) => {
    try {
      const allModels: Model[] = [];
      let pageNumber = 1;
      let totalPages = 1;

      // Fetch all pages using traditional pagination
      // Note: API returns { items, pagination } not { models, cursor }
      while (pageNumber <= totalPages) {
        const params: Record<string, string | number | boolean> = {
          includeLatestVersion: true,
          pageNumber,
          pageSize: 50
        };
        if (searchTerm) {
          params.search = searchTerm;
        }

        const response = await api.get<ModelsResponse>(`/labs/${id}/models`, { params });

        if (response.data.items && Array.isArray(response.data.items)) {
          allModels.push(...response.data.items);
        }

        totalPages = response.data.pagination?.totalPages || 1;
        pageNumber++;
      }

      return { items: allModels, pagination: { totalItems: allModels.length, totalPages, pageNumber: 1, pageSize: allModels.length } };
    } catch (error) {
      console.error('Error getting lab models with latest version:', error);
      return { items: [], pagination: { totalItems: 0, totalPages: 0, pageNumber: 1, pageSize: 20 } };
    }
  },

  // Get public model by id
  getModel: async (id: string, options?: { suppressErrors?: boolean }) => {
    try {
      const response = await api.get<Model>(`/models/${id}`);
      return response.data;
    } catch (error) {
      if (!options?.suppressErrors) {
        console.error('Error getting model:', error);
      }
      return null;
    }
  },

  getModelWithVersions: async (id: string) => {
    try {
      const response = await api.get<Model>(`/models/${id}`);
      // get the versions for the model
      const versions = await api.get<{items: ModelVersion[]}>(`/models/${id}/versions?sort=created:desc`);
      response.data.versions = versions.data.items;
      return response.data;
    } catch (error) {
      console.error('Error getting model:', error);
      return null;
    }
  },

  // Get model with all versions and latest checkpoints (Phase 0 - optimized)
  getModelWithLatestCheckpoints: async (id: string) => {
    try {
      const response = await api.get<Model>(`/models/${id}`, {
        params: { includeLatestCheckpoints: true }
      });
      // Use Zod to validate and transform the model data (dates, etc.)
      const model = ModelSchema.safeParse(response.data);
      if (model.success) {
        return model.data;
      } else {
        console.error('Invalid model data:', model.error.format());
        console.error('Full Zod error issues:', JSON.stringify(model.error.issues, null, 2));
        console.error('Raw response data:', response.data);
        return null;
      }
    } catch (error) {
      console.error('Error getting model with latest checkpoints:', error);
      return null;
    }
  },


  createModel: async (model: ModelCreateRequest) => {
    try {
      const response = await timeAsync('createModel.POST /models', () =>
        api.post<Model>('/models', model)
      );

      const result = ModelSchema.safeParse(response.data);
      if (!result.success) {
        console.error('Invalid model data from POST response:', result.error.format());
        return null;
      }

      // Verify model is readable via GET before returning (eventual consistency on cold start)
      await waitForResourceCreation(
        () => api.get<Model>(`/models/${response.data.id}`).then(r => r.data),
      );

      return result.data;
    } catch (error) {
      console.error('Error creating model:', error);
      return null;
    }
  },

  updateModel: async (model: ModelUpdateRequest): Promise<Model | null> => {
    try {
      const response = await api.put(`/models/${model.id}`, model);

      // Use Zod to validate and parse
      const result = ModelSchema.safeParse(response.data);
      if (!result.success) {
        throw new Error('Response is not a valid Model: ' + JSON.stringify(result.error.format()));
      }

      // Verify the update propagated to OpenSearch using centralized retry utility
      const fieldsToCheck: (keyof typeof result.data)[] = ['visibility', 'name', 'handle', 'description', 'picture'];
      const verifiedModel = await waitForResourceUpdate(
        () => api.get<Model>(`/models/${model.id}`).then(r => r.data),
        fieldsToCheck.reduce((acc, field) => {
          acc[field] = result.data[field];
          return acc;
        }, {} as Record<string, Model[keyof Model]>)
      );

      // Get the versions for the model
      const versions = await api.get<{items: ModelVersion[]}>(`/models/${model.id}/versions?sort=created:desc`);
      const filteredVersions = versions.data.items.filter(version => !version.id.includes('|ckpt.'));

      // Return verified model if available, otherwise return original
      const modelToReturn = verifiedModel || result.data;
      modelToReturn.versions = filteredVersions;

      if (!verifiedModel) {
        console.warn('Model update verification failed, returning original response data');
      }

      return modelToReturn;
    } catch (error) {
      console.error('Error updating model:', error);
      return null;
    }
  },

  deleteModel: async (id: string) => {
    try {
      await api.delete(`/models/${id}`);

      // Verify model is deleted from OpenSearch using centralized retry utility
      return await waitForResourceDeletion(
        () => api.get<Model>(`/models/${id}`).then(r => r.data)
      );
    } catch (error) {
      console.error('Error deleting model:', error);
      return false;
    }
  },

  publishModel: async (id: string, visibility: string, bettingStrategy?: string) => {
    try {
      const body: { visibility: string; bettingStrategy?: string } = { visibility };
      if (bettingStrategy) {
        body.bettingStrategy = bettingStrategy;
      }
      const response = await api.post<Model>(`/models/${id}/publish`, body);

      // Wait for the model to be published by checking versions[0].status using centralized retry utility
      const verifiedModel = await waitForFieldValue(
        () => api.get<Model>(`/models/${id}`).then(r => r.data),
        'versions[0].status',
        'published'
      );

      // Return verified model if available, otherwise return original response
      if (!verifiedModel) {
        console.warn('Model publish verification timed out, returning original response data');
      }

      return verifiedModel || response.data;
    } catch (error) {
      console.error('Error publishing model:', error);
      return null;
    }
  },

  updateModelStatus: async (id: string, status: 'active' | 'archived' | 'disabled') => {
    try {
      const response = await api.post<Model>(`/models/${id}/status`, { status });

      // Use waitForFieldValue to handle eventual consistency
      const verifiedModel = await waitForFieldValue(
        () => api.get<Model>(`/models/${id}`).then(r => r.data),
        'status',
        status
      );

      if (!verifiedModel) {
        console.warn('Model status update verification timed out');
      }

      return verifiedModel || response.data;
    } catch (error) {
      console.error('Error updating model status:', error);
      return null;
    }
  },

  // check if a handle is available
  checkHandleAvailability: async (handle: string) => {
    try {
      const response = await api.get<{available: boolean}>(`/handles/check?handle=${handle}`);
      return response.data.available;
    } catch (error) {
      console.error('Error checking handle availability:', error);
      return false;
    }
  },
  
  createModelVersion: async (id: string, features: Feature[]) => {
    try {
      const response = await api.post<ModelVersion>(`/models/${id}/versions`, { features });
      const result = ModelVersionSchema.safeParse(response.data);
      if (!result.success) {
        console.error('Invalid model version data from POST response:', result.error.format());
        return null;
      }
      return result.data;
    } catch (error) {
      console.error('Error creating model version:', error);
      return null;
    }
  },

  updateModelVersion: async (id: string, versionId: string, modelVersion: ModelVersionRequest) => {
    try {
      const response = await api.put<Model>(`/models/${id}/versions/${versionId}`, modelVersion);
      return response.data;
    } catch (error) {
      console.error('Error updating model version:', error);
      return null;
    }
  },

  deleteModelVersion: async (id: string, versionId: string) => {
    try {
      await api.delete(`/models/${id}/versions/${versionId}`);

      // Verify version is deleted from OpenSearch using centralized retry utility
      return await waitForResourceDeletion(
        () => api.get(`/models/${id}/versions/${versionId}`).then(r => r.data)
      );
    } catch (error) {
      console.error('Error deleting model version:', error);
      return false;
    }
  },

  getModelVersion: async (id: string, versionId: string, options?: { includeCheckpoints?: boolean; suppressErrors?: boolean }) => {
    try {
      const response = await api.get<ModelVersion>(`/models/${id}/versions/${versionId}`);
      // Use Zod to validate and transform the version data (dates, etc.)
      const version = ModelVersionSchema.safeParse(response.data);
      if (version.success) {
        if (options?.includeCheckpoints) {
          // Get checkpoints if not requested
          const checkpoints = await api.get<{items: Checkpoint[]}>(`/models/${id}/versions/${versionId}/checkpoints?sort=created:desc`);
          version.data.checkpoints = checkpoints.data.items;
        }
        return version.data;
      } else {
        if (!options?.suppressErrors) {
          console.error('Invalid model version data:', version.error.format());
        }
        return null;
      }
    } catch (error) {
      if (!options?.suppressErrors) {
        console.error('Error getting model version:', error);
      }
      return null;
    }
  },

  getModelVersions: async (id: string) => {
    try {
      const response = await api.get<{items: ModelVersion[]}>(`/models/${id}/versions?sort=created:desc`);
      // Filter out checkpoint records
      const filteredVersions = {
        ...response.data,
        items: response.data.items.filter(version => !version.id.includes('|ckpt.'))
      };
      return filteredVersions;
    } catch (error) {
      console.error('Error getting model versions:', error);
      return null;
    }
  },

  getModelPicks: async (id: string, params?: Record<string, string>) => {
    try {
      const response = await api.get(`/models/${id}/picks`, { params });
      return response.data;
    } catch {
      return null;
    }
  },

  createCheckpoint: async (id: string, checkpoint: CheckpointRequest) => {
    try {
      const response = await api.post<Checkpoint>(`/models/${id}/checkpoints`, checkpoint);
      const result = CheckpointSchema.safeParse(response.data);
      if (!result.success) {
        console.error('Invalid checkpoint data from POST response:', result.error.format());
        return null;
      }
      return result.data;
    } catch (error) {
      console.error('Error creating checkpoint:', error);
      return null;
    }
  },

  getCheckpoint: async (modelId: string, versionId: string, checkpointId: string, options?: { suppressErrors?: boolean }) => {
    try {
      const response = await api.get<Checkpoint>(`/models/${modelId}/versions/${versionId}/checkpoints/${checkpointId}`);
      // Use Zod to validate and parse checkpoint
      const checkpoint = CheckpointSchema.safeParse(response.data);
      if (checkpoint.success) {
        return checkpoint.data;
      } else {
        if (!options?.suppressErrors) {
          console.error('Invalid checkpoint data:', checkpoint.error.format());
        }
        return null;
      }
    } catch (error) {
      if (!options?.suppressErrors) {
        console.error('Error getting checkpoints:', error);
      }
      return null;
    }
  },

  getCheckpoints: async (modelId: string, versionId: string) => {
    try {
      const response = await api.get<{items: Checkpoint[]}>(`/models/${modelId}/versions/${versionId}/checkpoints?sort=created:desc`);
      // Use Zod to validate and parse checkpoints

      const checkpoints = response.data.items.map(checkpoint => {
        const result = CheckpointSchema.safeParse(checkpoint);
        if (result.success) {
          return result.data;
        } else {
          console.error('Invalid checkpoint data:', result.error.format());
          return checkpoint; // Return original if validation fails
        }
      });
      //sort checkpoints by create date descending
      checkpoints.sort((a, b) => Date.parse(String(b.created)) - Date.parse(String(a.created)));
      return checkpoints;
    } catch (error) {
      console.error('Error getting checkpoints:', error);
      return [];
    }
  },

  createFeature: async (id: string, feature: FeatureRequest) => {
    try {
      const response = await timeAsync(`createFeature.POST /models/${id}/features`, () =>
        api.post<Feature>(`/models/${id}/features`, feature)
      );
      return response.data;
    } catch (error) {
      console.error('Error creating feature:', error);
      return null;
    }
  },

  updateFeature: async (id: string, featureId: string, feature: FeatureRequest) => {
    try {
      const response = await api.put<Feature>(`/models/${id}/features/${featureId}`, feature);
      return response.data;
    } catch (error) {
      console.error('Error updating feature:', error);
      return null;
    }
  },

  deleteFeature: async (id: string, featureId: string) => {
    try {
      await api.delete(`/models/${id}/features/${featureId}`);
      return true;
    } catch (error) {
      console.error('Error deleting feature:', error);
      return false;
    }
  },

  cancelOperation: async (id: string, versionId: string, checkpointId: string) => {
    try {
      console.log('Cancelling operation for model:', id, 'version:', versionId, 'checkpoint:', checkpointId);
      const response = await api.put(`/models/${id}/versions/${versionId}/checkpoints/${checkpointId}/operation/cancel`);
      console.log('Cancel operation response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error canceling operation:', error);
      return null;
    }
  },

  sendDebugEvent: async (event: string) => {
    try {
      const response = await api.post('/debug/send-event', event);
      return response.data;
    } catch (error) {
      console.error('Error sending debug event:', error);
      return null;
    }
  },

  getBettingStrategies: async (status?: 'active' | 'archived'): Promise<BettingStrategy[]> => {
    try {
      const params: Record<string, string> = {};
      if (status) params.status = status;
      const response = await api.get<BettingStrategy[]>('/betting-strategies', { params });
      return response.data;
    } catch (error) {
      console.error('Error getting betting strategies:', error);
      return [];
    }
  },

  createBettingStrategy: async (data: Omit<BettingStrategy, 'id' | 'created' | 'lastUpdated'>): Promise<BettingStrategy | null> => {
    try {
      const response = await api.post<BettingStrategy>('/betting-strategies', data);
      return response.data;
    } catch (error) {
      console.error('Error creating betting strategy:', error);
      return null;
    }
  },

  updateBettingStrategy: async (id: string, data: Partial<Omit<BettingStrategy, 'id' | 'created' | 'lastUpdated'>>): Promise<BettingStrategy | null> => {
    try {
      const response = await api.put<BettingStrategy>(`/betting-strategies/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating betting strategy:', error);
      return null;
    }
  },

  deleteBettingStrategy: async (id: string): Promise<boolean> => {
    try {
      await api.delete(`/betting-strategies/${id}`);
      return true;
    } catch (error) {
      console.error('Error deleting betting strategy:', error);
      return false;
    }
  },

  getStats: async (status?: 'active' | 'archived', domain?: string, outcome?: string): Promise<Stat[]> => {
    try {
      const params: Record<string, string> = {};
      if (status) params.status = status;
      if (domain) params.domain = domain;
      if (outcome) params.outcome = outcome;
      const response = await api.get<Stat[]>('/stats', { params });
      return response.data;
    } catch (error) {
      console.error('Error getting stats:', error);
      return [];
    }
  },

  getStat: async (id: string): Promise<Stat | null> => {
    try {
      const response = await api.get<Stat>(`/stats/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error getting stat:', error);
      return null;
    }
  },

  createStat: async (data: Omit<Stat, 'id' | 'created' | 'lastUpdated'>): Promise<Stat | null> => {
    try {
      const response = await api.post<Stat>('/stats', data);
      return response.data;
    } catch (error) {
      console.error('Error creating stat:', error);
      return null;
    }
  },

  updateStat: async (id: string, data: Partial<Omit<Stat, 'id' | 'created' | 'lastUpdated'>>): Promise<Stat | null> => {
    try {
      const response = await api.put<Stat>(`/stats/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating stat:', error);
      return null;
    }
  },

  deleteStat: async (id: string): Promise<boolean> => {
    try {
      await api.delete(`/stats/${id}`);
      return true;
    } catch (error) {
      console.error('Error deleting stat:', error);
      return false;
    }
  },

});